import { useState } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';

const CONVERSION_TYPES = {
  sale: { label: '🟢 For Sale', desc: 'Set a fixed price' },
  rent: { label: '🔵 For Rent', desc: 'Earn daily rental income' },
  auction: { label: '🔴 Auction', desc: 'Let bidders compete for your item' },
  barter: { label: '🔄 Barter', desc: 'Trade for items you want' },
  free: { label: '🆓 Free', desc: 'Share with your community' }
};

export default function ListingTypeConverter({ listing, isOpen, onClose, onConvert }) {
  const [converting, setConverting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [action, setAction] = useState('convert'); // 'convert' or 'clone'

  if (!isOpen || !listing) return null;

  const currentType = listing.listing_type;
  const availableTypes = Object.keys(CONVERSION_TYPES).filter(t => t !== currentType);

  // Calculate conversion fee logic
  const getConversionCost = (newType) => {
    // Free → anything paid: full fee
    if (currentType === 'free' && newType !== 'free') {
      return { amount: newType === 'barter' ? 10 : 0, note: 'Subscription required' };
    }
    // Barter → paid: no extra fee (already paid $10)
    if (currentType === 'barter' && ['sale', 'rent', 'auction'].includes(newType)) {
      return { amount: 0, note: 'Already paid barter fee' };
    }
    // Paid → barter: charge $10 + subscription
    if (['sale', 'rent', 'auction'].includes(currentType) && newType === 'barter') {
      return { amount: 10, note: 'Barter fee + subscription required' };
    }
    // Paid → free: free
    if (['sale', 'rent', 'auction'].includes(currentType) && newType === 'free') {
      return { amount: 0, note: 'No fee' };
    }
    // Paid ↔ paid: no extra fee
    if (['sale', 'rent', 'auction'].includes(currentType) && ['sale', 'rent', 'auction'].includes(newType)) {
      return { amount: 0, note: 'No additional fee' };
    }
    return { amount: 0, note: '' };
  };

  const handleConvert = async () => {
    if (!selectedType) {
      toast.error('Select a listing type');
      return;
    }

    setConverting(true);
    try {
      const response = await API.patch(`/listings/${listing.id}/convert-type`, {
        newListingType: selectedType,
        action
      });

      if (action === 'clone') {
        toast.success(`Cloned as ${CONVERSION_TYPES[selectedType].label}`);
      } else {
        toast.success(`Converted to ${CONVERSION_TYPES[selectedType].label}`);
      }

      onConvert?.(response.data.listing || response.data.newListing);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 998,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: 16,
          padding: 32,
          zIndex: 999,
          maxWidth: 520,
          width: 'calc(100% - 32px)',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 56px rgba(26,18,10,0.13)',
          animation: 'scaleIn 0.3s ease'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
            Change Listing Type
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>
            {listing.title}
          </p>
          <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 12 }}>
            Currently: <strong>{CONVERSION_TYPES[currentType]?.label}</strong>
          </div>
        </div>

        {/* Type Options */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 16 }}>
            Convert to
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {availableTypes.map(type => {
              const cost = getConversionCost(type);
              const isSelected = selectedType === type;

              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  style={{
                    padding: '16px',
                    textAlign: 'left',
                    border: isSelected ? '2px solid var(--cherry)' : '1px solid var(--border)',
                    background: isSelected ? 'var(--cherry-light)' : 'white',
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                      {CONVERSION_TYPES[type].label}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                      {CONVERSION_TYPES[type].desc}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--cherry)', marginTop: 6, fontWeight: 600 }}>
                      {cost.note && `💡 ${cost.note}`}
                    </div>
                  </div>
                  {cost.amount > 0 && (
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cherry)' }}>
                      ${cost.amount}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Selection */}
        <div style={{ marginBottom: 24, padding: '16px', background: 'var(--cream)', borderRadius: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 12 }}>
            What would you like to do?
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="action"
                value="convert"
                checked={action === 'convert'}
                onChange={e => setAction(e.target.value)}
              />
              <span style={{ fontSize: 13 }}>Convert this listing</span>
            </label>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="action"
                value="clone"
                checked={action === 'clone'}
                onChange={e => setAction(e.target.value)}
              />
              <span style={{ fontSize: 13 }}>Clone as new listing</span>
            </label>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 8 }}>
            {action === 'convert'
              ? 'Changes the type of this listing'
              : 'Creates a new listing with the same details'}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handleConvert}
            disabled={!selectedType || converting}
            className="btn btn-cherry"
            style={{ flex: 1 }}
          >
            {converting ? '⏳ Processing...' : `✓ ${action === 'clone' ? 'Clone' : 'Convert'}`}
          </button>
          <button
            onClick={onClose}
            disabled={converting}
            className="btn btn-ghost"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
